import { useMemo, useState } from 'react';
import { Building2, Plus, Shield, User, Users, X } from 'lucide-react';
import { NotionDrawer } from '../ui/notion/NotionDrawer';
import { NotionSelect } from '../ui/notion/NotionSelect';
import { HelpTip } from '../ui/HelpTip';
import { useCalendarSharingStore } from '../../lib/stores/calendarSharingStore';
import { LEVEL_HELP, LEVEL_LABELS, sharesOfOwner, type CalendarShare, type ShareLevel, type ShareSubjectType } from '../../lib/calendar/sharing';
import { useApp } from '../AppProvider';

const LEVELS: ShareLevel[] = ['busy', 'details', 'edit'];
const LEVEL_OPTIONS = LEVELS.map(l => ({ value: l, label: LEVEL_LABELS[l] }));
const SUBJECT_ICON: Record<string, typeof User> = { user: User, department: Users, role: Shield, everyone: Building2 };
const SEP = 'border-t border-[rgba(55,53,47,0.09)] dark:border-white/10';

/** Testo leggibile per il destinatario o il proprietario di una regola. */
export function useShareNames() {
  const { people, departments, roles } = useCalendarSharingStore();
  return (type: string, id: string | null): string => {
    if (type === 'everyone') return "Tutta l'azienda";
    if (type === 'user') return people.find(p => p.id === id)?.name || 'Persona';
    if (type === 'department') return `Reparto ${departments.find(d => d.id === id)?.name || ''}`.trim();
    return `Ruolo ${roles.find(r => r.id === id)?.name || ''}`.trim();
  };
}

/** Le possibili persone, reparti e ruoli a cui dare accesso (valore "tipo:id"). */
export function useSubjectOptions(excludeUserId?: string) {
  const { people, departments, roles } = useCalendarSharingStore();
  return useMemo(() => ([
    { group: 'Azienda', items: [{ value: 'everyone:', label: "Tutta l'azienda" }] },
    { group: 'Reparti', items: departments.map(d => ({ value: `department:${d.id}`, label: d.name })) },
    { group: 'Ruoli', items: roles.map(r => ({ value: `role:${r.id}`, label: r.name })) },
    { group: 'Persone', items: people.filter(p => p.id !== excludeUserId).map(p => ({ value: `user:${p.id}`, label: p.name })) },
  ].filter(g => g.items.length)), [people, departments, roles, excludeUserId]);
}

/** Il contenuto del pannello Condividi, riusabile anche nel Profilo (scheda Condivisione). */
export function ShareMyCalendarPanel() {
  const { ctx, people, shares, saveShare, deleteShare, error } = useCalendarSharingStore();
  const { toast } = useApp();
  const nameOf = useShareNames();
  const meId = ctx?.meId || '';
  const options = useSubjectOptions(meId);
  const [subject, setSubject] = useState('');
  const [level, setLevel] = useState<ShareLevel>('details');
  const [busy, setBusy] = useState(false);

  const me = people.find(p => p.id === meId);
  const mine = shares.filter(s => s.owner_type === 'user' && s.owner_id === meId);
  const inherited = me ? sharesOfOwner(shares, me).filter(s => s.owner_type !== 'user') : [];

  const add = async () => {
    if (!subject || busy) return;
    const [type, id] = subject.split(':');
    setBusy(true);
    const ok = await saveShare({ owner_type: 'user', owner_id: meId, subject_type: type as ShareSubjectType, subject_id: id || null, level });
    setBusy(false);
    if (ok) { setSubject(''); toast.success('Condivisione salvata'); } else toast.error(error || 'Condivisione non salvata');
  };

  const row = (s: CalendarShare, editable: boolean) => {
    const Icon = SUBJECT_ICON[s.subject_type] || User;
    return (
      <div key={s.id} className="group flex min-h-[34px] items-center gap-2 rounded px-1.5 py-0.5 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04]">
        <Icon className="h-4 w-4 shrink-0 text-[#787774]" strokeWidth={1.5} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] text-[#37352F] dark:text-white">{nameOf(s.subject_type, s.subject_id)}</div>
          {!editable && <div className="text-[11px] text-[#787774]">Regola dell'amministratore per {nameOf(s.owner_type, s.owner_id).toLowerCase()}</div>}
        </div>
        {editable ? (
          <>
            <NotionSelect
              variant="flat"
              aria-label="Livello"
              value={s.level}
              options={LEVEL_OPTIONS}
              onChange={e => void saveShare({ owner_type: 'user', owner_id: meId, subject_type: s.subject_type, subject_id: s.subject_id, level: e.target.value as ShareLevel })}
            />
            <button type="button" aria-label="Togli l'accesso" onClick={() => void deleteShare(s.id)}
              className="rounded p-1 text-[#787774] opacity-0 hover:bg-[rgba(55,53,47,0.08)] group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100">
              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </>
        ) : <span className="pr-1.5 text-[13px] text-[#787774]">{LEVEL_LABELS[s.level]}</span>}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-[#787774]">Il tuo calendario contiene le task commerciali assegnate a te. Scegli chi altro può vederlo.</p>

      <section>
        <div className="mb-1 flex items-center gap-1.5 px-1.5 text-xs font-medium text-[#787774]">
          Chi può vederlo
          <HelpTip
            title="Livelli di condivisione"
            text={<span><b>Solo occupato</b>: {LEVEL_HELP.busy}<br /><b>Vede i dettagli</b>: {LEVEL_HELP.details}<br /><b>Può modificare</b>: {LEVEL_HELP.edit}</span>}
            example='Dai "Solo occupato" a tutta l&apos;azienda e "Può modificare" alla tua assistente.'
          />
        </div>
        <div className={`flex flex-col ${SEP} pt-1`}>
          {mine.map(s => row(s, true))}
          {inherited.map(s => row(s, false))}
          <div className="flex min-h-[34px] items-center gap-2 rounded px-1.5 py-0.5">
            <Shield className="h-4 w-4 shrink-0 text-[#787774]" strokeWidth={1.5} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-[#37352F] dark:text-white">Amministratori e responsabili</div>
              <div className="text-[11px] text-[#787774]">Chi ha il permesso "Vede i calendari di tutti"</div>
            </div>
            <span className="pr-1.5 text-[13px] text-[#787774]">Sempre</span>
          </div>
          <div
            className={`mt-1 flex min-h-[34px] items-center gap-2 rounded px-1.5 py-0.5 ${SEP}`}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }}
          >
            <Plus className="h-4 w-4 shrink-0 text-[#787774]" strokeWidth={1.5} />
            <select aria-label="Con chi" value={subject} onChange={e => setSubject(e.target.value)}
              className="min-w-0 flex-1 cursor-pointer rounded bg-transparent py-0.5 text-[13px] text-[#37352F] hover:bg-[rgba(55,53,47,0.08)] focus:outline-none dark:text-white">
              <option value="">Aggiungi persona, reparto, ruolo o tutta l'azienda</option>
              {options.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.items.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                </optgroup>
              ))}
            </select>
            <NotionSelect variant="flat" aria-label="Livello da dare" value={level} options={LEVEL_OPTIONS} onChange={e => setLevel(e.target.value as ShareLevel)} />
            <button type="button" disabled={!subject || busy} onClick={() => void add()}
              className="rounded bg-[#37352F] px-2.5 py-1 text-xs font-medium text-white hover:bg-black disabled:opacity-30">Aggiungi</button>
          </div>
        </div>
      </section>

      <p className="text-[11px] text-[#787774]">Cosa vedono i colleghi dei tuoi eventi Google o Outlook lo scegli in Profilo › Il mio calendario.</p>
    </div>
  );
}

export function ShareMyCalendarDialog({ onClose }: { onClose: () => void }) {
  return (
    <NotionDrawer isOpen onClose={onClose} title="Condividi il mio calendario" width="md">
      <ShareMyCalendarPanel />
    </NotionDrawer>
  );
}
